/**
 * El rango de fechas que un campo `type="date"` puede producir.
 *
 * ⚠ **Un `<input type="date">` acepta un año de cinco cifras.** Tecleando en el
 * hueco del año sale `20226-09-10`, el navegador no se queja, y el servidor
 * responde 422 sobre un formulario que se veía correcto. No es una hipótesis:
 * pasó al crear la primera tarea con soportes.
 *
 * Se acota por los dos lados. `min` y `max` hacen que el selector no llegue ahí y
 * que el navegador marque el campo como fuera de rango; `fechaFueraDeRango()` es
 * la comprobación de verdad, porque un valor tecleado se puede enviar igual.
 */

// ⚠ **1950 y no 2000, y no es un detalle: con 2000 no se podía registrar la fecha
// de nacimiento de nadie nacido antes.** El tope existe para cazar el año de cinco
// cifras que produce un `<input type="date">` al teclear, y para eso 1950 sirve
// igual: `20226-09-10` sigue quedando fuera. Si alguna vez hace falta registrar a
// alguien nacido antes, se baja esta línea.
export const FECHA_MINIMA = "1950-01-01"
export const FECHA_MAXIMA = "2099-12-31"

/** `true` cuando el valor existe y no cae dentro del rango admitido. */
export function fechaFueraDeRango(valor: string): boolean {
  if (valor === "") return false
  // La forma antes que el rango: `20226-09-10` ni siquiera tiene cuatro cifras de
  // año, y compararlo como cadena funcionaría por accidente.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(valor)) return true
  return valor < FECHA_MINIMA || valor > FECHA_MAXIMA
}

/** Hoy en `YYYY-MM-DD` y en hora LOCAL.
 *
 * ⚠ `toISOString()` daría UTC, y en Caracas —cuatro horas detrás— desde las ocho
 * de la noche devolvería el día siguiente: una tarea que vence hoy se pintaría
 * como vencida cada tarde. */
export function hoyLocal(d = new Date()): string {
  const dos = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`
}

/** Un `YYYY-MM-DD` como marca UTC de medianoche, o `NaN` si no tiene esa forma.
 *
 * ⚠ `Date.UTC` y no el constructor local: aquí sólo se restan dos fechas, y en
 * hora local un cambio de horario mete un desfase de una hora que al dividir
 * entre un día se convierte en un día de más o de menos. */
function marca(fecha: string): number {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha)
  if (partes === null) return Number.NaN
  return Date.UTC(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]))
}

/** Días entre dos fechas `YYYY-MM-DD`. Negativo si la primera ya pasó. */
export function diasHasta(fecha: string, desde = hoyLocal()): number {
  return Math.round((marca(fecha) - marca(desde)) / 86_400_000)
}

export type Urgencia = { texto: string; tono: "peligro" | "aviso" | "neutro" }

/**
 * Cómo se dice un vencimiento en una insignia.
 *
 * ⚠ **En días y no como fecha.** «2026-08-21» obliga a restar mentalmente para
 * saber si eso es hoy; «Vence hoy» no. La fecha exacta sigue estando en la ficha,
 * que es donde se necesita el dato y no el juicio.
 */
export function urgenciaDe(vence: string | null, desde = hoyLocal()): Urgencia {
  if (vence === null) return { texto: "Sin fecha", tono: "neutro" }
  const dias = diasHasta(vence, desde)
  // Una fecha con forma rara no se convierte en «En NaN días»: se dice que no se
  // sabe, que es lo que pasa. Un `NaN` pintado en pantalla se lee como un fallo
  // del sistema y no como un dato malo.
  if (!Number.isFinite(dias)) return { texto: "Sin fecha", tono: "neutro" }
  if (dias < 0)
    return { texto: dias === -1 ? "Venció ayer" : `Venció hace ${-dias} d`, tono: "peligro" }
  if (dias === 0) return { texto: "Vence hoy", tono: "peligro" }
  if (dias === 1) return { texto: "Vence mañana", tono: "aviso" }
  if (dias <= 7) return { texto: `En ${dias} días`, tono: "aviso" }
  return { texto: `En ${dias} días`, tono: "neutro" }
}

/**
 * Una fecha ISO como se lee aquí: `DD/MM/AAAA`.
 *
 * ⚠ **Se corta la cadena; NO se construye un `Date`.** `new Date("2026-01-15")` se
 * interpreta como medianoche UTC, y en Caracas —cuatro horas detrás— eso es el día
 * 14 a las ocho de la tarde: toda fecha se pintaría un día antes. Es el mismo motivo
 * por el que `hoyLocal()` no usa `toISOString()`.
 *
 * Admite tanto `2026-01-15` como `2026-01-15T10:30:00`; lo que no tenga esa forma se
 * devuelve tal cual, que es mejor que inventarse una fecha o pintar «Invalid Date».
 */
export function comoFecha(iso: string | null | undefined): string {
  if (!iso) return ""
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (m === null) return iso
  return `${m[3]}/${m[2]}/${m[1]}`
}

/** Caracas: UTC-4 fijo. Venezuela no tiene horario de verano desde 2016. */
const HUSO_VE_MINUTOS = -4 * 60

/**
 * Una marca de tiempo como se lee aquí: `DD/MM/AAAA HH:MM`, en HORA DE CARACAS.
 *
 * ⚠ **El servidor manda UTC sin zona** (`2026-08-21T19:06:33`): guarda
 * `datetime.now(UTC)` y la sesión de MySQL va en UTC (`backend/src/app/db.py`). Hasta
 * el 10/10 esto se pintaba tal cual y toda hora del sistema salía cuatro horas
 * adelantada. Si la marca trae zona (`Z`, `+00:00`, `-04:00`), se respeta.
 *
 * ⚠ **Se convierte con aritmética, no con el huso del navegador.** Con `new Date()` y
 * `getHours()` la hora dependería de dónde esté abierto el navegador; aquí la misma
 * marca se lee igual en Caracas y en un portátil configurado en Madrid.
 *
 * `segundos` para la bitácora de auditoría, donde dos apuntes del mismo minuto son
 * dos cosas distintas y el orden entre ellos importa.
 */
export function comoFechaHora(iso: string | null | undefined, segundos = false): string {
  if (!iso) return ""
  const m =
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.exec(
      iso,
    )
  if (m === null) return comoFecha(iso)
  const n = (i: number) => Number(m[i] ?? 0)
  const zona = m[7]
  let desfase = 0 // minutos que la marca va por delante de UTC
  if (zona && zona !== "Z") {
    const z = /^([+-])(\d{2}):?(\d{2})$/.exec(zona)
    if (z) desfase = (z[1] === "-" ? -1 : 1) * (Number(z[2]) * 60 + Number(z[3]))
  }
  const utc = Date.UTC(n(1), n(2) - 1, n(3), n(4), n(5), n(6)) - desfase * 60_000
  const ve = new Date(utc + HUSO_VE_MINUTOS * 60_000)
  const dos = (n: number) => String(n).padStart(2, "0")
  const fecha = `${dos(ve.getUTCDate())}/${dos(ve.getUTCMonth() + 1)}/${ve.getUTCFullYear()}`
  const hora = `${dos(ve.getUTCHours())}:${dos(ve.getUTCMinutes())}`
  return `${fecha} ${segundos ? `${hora}:${dos(ve.getUTCSeconds())}` : hora}`
}

/** Una marca del servidor como instante: sin zona es UTC (ver `comoFechaHora`).
 *
 * ⚠ `new Date("2026-08-21T19:06:33")` la leería como hora LOCAL del navegador: cuatro
 * horas de desfase en Caracas, y otras tantas distintas en cualquier otro sitio. */
export function instanteDe(iso: string): Date {
  return new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`)
}
